import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { FormField, FormToggle, ColorPickerField } from '@/shared/components'
import type { Action } from '@/shared/types/admin'

const actionSchema = z.object({
  libelle: z.string().min(1, 'Le libellé est obligatoire'),
  heures: z.number().min(0),
  minutes: z.number().min(0).max(59),
  actif: z.boolean(),
})

type ActionFormValues = z.infer<typeof actionSchema>

interface ActionFormProps {
  action?: Action
  onSubmit: (data: { libelle: string; dureeMinutes: number; couleur?: string; actif: boolean }) => void
  isPending: boolean
}

export function ActionForm({ action, onSubmit, isPending }: ActionFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [couleur, setCouleur] = useState(action?.couleur || '#BBDEFB')

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ActionFormValues>({
    resolver: zodResolver(actionSchema),
    defaultValues: {
      libelle: '',
      heures: 0,
      minutes: 0,
      actif: true,
    },
  })

  useEffect(() => {
    if (action) {
      setValue('libelle', action.libelle)
      setValue('heures', Math.floor(action.dureeMinutes / 60))
      setValue('minutes', action.dureeMinutes % 60)
      setValue('actif', action.actif)
      if (action.couleur) setCouleur(action.couleur)
    }
  }, [action, setValue])

  const heures = watch('heures')
  const minutes = watch('minutes')
  const totalMinutes = (heures || 0) * 60 + (minutes || 0)

  const handleFormSubmit = (values: ActionFormValues) => {
    onSubmit({
      libelle: values.libelle.trim(),
      dureeMinutes: (values.heures || 0) * 60 + (values.minutes || 0),
      couleur: couleur || undefined,
      actif: values.actif,
    })
  }

  const inputClass =
    'h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25'

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="mx-auto max-w-lg space-y-6">
      <div className="rounded-xl border border-neutral-200 bg-white p-6 space-y-5">
        <h2 className="text-base font-semibold text-neutral-900">
          {t('administration.sections.actions.form.informationTitle', "Informations de l'action")}
        </h2>

        <FormField
          label={t('administration.sections.actions.form.label', "Libellé de l'action")}
          error={errors.libelle?.message}
          required
        >
          <input {...register('libelle')} className={inputClass} />
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <FormField label={t('administration.sections.actions.form.hours', 'Heures')}>
            <input type="number" min={0} {...register('heures', { valueAsNumber: true })} className={inputClass} />
          </FormField>
          <FormField label={t('administration.sections.actions.form.minutes', 'Minutes')}>
            <input type="number" min={0} max={59} {...register('minutes', { valueAsNumber: true })} className={inputClass} />
          </FormField>
        </div>

        {totalMinutes > 0 && (
          <p className="text-xs text-neutral-500">
            {t('administration.sections.actions.form.totalMinutes', 'Total: {{count}} minutes', { count: totalMinutes })}
          </p>
        )}

        <ColorPickerField
          label={t('administration.sections.actions.form.color', 'Couleur')}
          value={couleur}
          onChange={setCouleur}
        />

        <FormToggle
          label={t('administration.sections.actions.form.active', 'Action active')}
          checked={watch('actif')}
          onChange={(v) => setValue('actif', v)}
        />
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate('/administration/actions')}
          className="rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
        >
          {t('administration.sections.actions.form.cancel', 'Annuler')}
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isPending
            ? t('administration.sections.actions.form.saving', 'Enregistrement...')
            : action
              ? t('administration.sections.actions.form.save', 'Enregistrer')
              : t('administration.sections.actions.form.create', "Créer l'action")}
        </button>
      </div>
    </form>
  )
}
